import React, { useEffect } from 'react';
import { theme } from '../../theme';
import { useMessageGetter } from 'react-message-context';
import Card from '../../components/common/Card';
import styled from 'styled-components/macro';
import { NotificationType, useApp, useAppDispatch } from '../../context/appContext';
import { getMenuItemPathBySlug } from '../../utils/wordpress';
import { Slugs } from '../../constants/knownSlugs';
import { FlexColumn, FlexRow } from '../../components/basic/Flex';
import { LinkButton } from '../../components/common';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loadBillingHistory } from '../../context/kennoTenantActions';
import { LOAD_STATES } from '../../context/common';
import { showNotification } from '../../context/appActions';
import Loader from '../../components/basic/Loader';
import { formatDate, formatEuros } from '../../utils';

const Cell = styled(FlexColumn)`
  width: 50%;
  height: min-content;
`;
const CellFull = styled(Cell)`
  width: 100%;
  margin: 6px 0;
`;
const CellTitle = styled.p`
  font-size: 16px;
  margin-bottom: 12px;
  font-family: ${(props) => props.theme.text.primaryFont};
`;
const BoldCellTitle = styled(CellTitle)`
  margin-top: 0;
  font-weight: 700;
`;
const CellValue = styled.p`
  font-size: 21px;
  margin: 0;
`;
const Info = styled.p`
  margin-top: 30px;
  font-size: 14px;
  font-family: ${(props) => props.theme.text.primaryFont};
`;

const RentCard = () => {
  const t = useMessageGetter('home.grid.rent');
  const [{ billingHistory }, dispatch] = useKennoTenant();
  const [{ menu }] = useApp();
  const appDispatch = useAppDispatch();

  useEffect(() => {
    loadBillingHistory(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (billingHistory.loadState === LOAD_STATES.ERROR) {
      showNotification(appDispatch, t('loadfailure'), NotificationType.ERROR);
    }
  }, [billingHistory.loadState]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardContent = () => {
    if (billingHistory.loadState !== LOAD_STATES.READY) {
      return <Loader />;
    }
    const currentBillingMonth = billingHistory.items?.find((item) => item.isCurrent);
    const billedThisMonth = billingHistory.items?.find((item) => item.isCurrent)?.billed;
    const currentTotalPaid = currentBillingMonth?.sumAfter;
    const hasPaidInAdvance = currentTotalPaid && currentTotalPaid > 0;
    const hasUnpaid = currentTotalPaid && currentTotalPaid <= 0;

    return (
      <FlexRow wrap="wrap">
        {billedThisMonth?.map((bill) => (
          <>
            <Cell>
              <CellTitle>{bill.reasonName}</CellTitle>
              <CellValue>{formatEuros(bill.amountEuros)}</CellValue>
            </Cell>
            <Cell>
              <CellTitle>{t('due_date_label')}</CellTitle>
              <CellValue>{formatDate(bill.dueDate)}</CellValue>
            </Cell>
          </>
        ))}
        <Info>{t('payment_delay_description')}</Info>
        {hasUnpaid && (
          <CellFull>
            <BoldCellTitle>{t('total_to_pay')}</BoldCellTitle>
            <CellValue>{`${formatEuros(-currentTotalPaid)}`}</CellValue>
          </CellFull>
        )}
        {hasPaidInAdvance && (
          <CellFull>
            <BoldCellTitle>{t('paid_in_advance')}</BoldCellTitle>
            <CellValue>{`+${formatEuros(currentTotalPaid)}`}</CellValue>
          </CellFull>
        )}
      </FlexRow>
    );
  };

  return (
    <Card
      title={t('title')}
      background={theme().colors.mint}
      mainButton={
        <LinkButton
          size="small"
          background="transparent"
          to={getMenuItemPathBySlug(menu.items, Slugs.PAYMENT)}
          iconRight
          icon="arrowForward"
        >
          {t('action.label')}
        </LinkButton>
      }
      secondaryButton={
        <LinkButton size="small" to={getMenuItemPathBySlug(menu.items, Slugs.BILLING)} primary>
          {t('action2.label')}
        </LinkButton>
      }
    >
      {cardContent()}
    </Card>
  );
};

export default RentCard;
